// src/components/exercise/exercises/FillBlankExercise.tsx
import React, { useEffect, useRef, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { QuestionBox, FeedbackBox } from './TrueFalseExercise';
import { useThemePalette } from '../../../hooks/useThemePalette';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

const FillBlankExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C = useThemePalette();
  const [value,   setValue]   = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue('');
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [exercise]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSubmitted) return;
    const v = e.target.value;
    setValue(v);
    onChange({ type: 'fill_blank', value: v });
  };

  let borderColor = focused ? C.accent : C.border;
  let inputBg     = C.card;
  let glow        = focused ? '0 0 0 3px rgba(79,140,247,0.12)' : 'none';
  if (isSubmitted && grade) {
    borderColor = grade.isCorrect ? C.success : C.error;
    inputBg     = grade.isCorrect ? 'rgba(52,211,153,0.06)' : 'rgba(248,113,113,0.05)';
    glow        = 'none';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <label style={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Your Answer
        </label>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={isSubmitted}
          placeholder="Type your answer here…"
          style={{
            padding: '13px 16px', borderRadius: '12px',
            border: `1.5px solid ${borderColor}`,
            background: inputBg, color: C.text, fontSize: '1rem',
            outline: 'none', transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            boxShadow: glow, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
          onKeyDown={(e) => { e.stopPropagation(); }}
        />
      </div>
      {isSubmitted && grade && (
        <FeedbackBox
          isCorrect={grade.isCorrect}
          correctDisplay={!grade.isCorrect ? grade.correctAnswerDisplay : undefined}
        />
      )}
    </div>
  );
};

export default FillBlankExercise;
